import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusCircle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/error-message";
import { stockAdjustmentService } from "@/services/stock-adjustment";

import { StockAdjustmentCancelDialog } from "./stock-adjustment-cancel-dialog";
import { StockAdjustmentDialog } from "./stock-adjustment-dialog";
import { StockAdjustmentSummaryCards } from "./stock-adjustment-summary-cards";
import { StockAdjustmentTableCard } from "./stock-adjustment-table-card";

import type { StockAdjustment } from "@artisancode/api-types";

export function StockAdjustmentList() {
  const queryClient = useQueryClient();

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [itemTypeFilter, setItemTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bucketFilter, setBucketFilter] = useState("all");

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [cancellingAdjustment, setCancellingAdjustment] =
    useState<StockAdjustment | null>(null);

  // Query adjustments
  const {
    data: adjustmentsRes,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["stock-adjustments", "list"],
    queryFn: () => stockAdjustmentService.list({ per_page: 200 }),
  });

  const adjustments = adjustmentsRes?.items || [];

  // Summary Metrics (only completed contribute to net impact)
  const totalCount = adjustments.length;
  const plusCount = adjustments.filter(
    (a) => a.status === "completed" && a.difference > 0,
  ).length;
  const minusCount = adjustments.filter(
    (a) => a.status === "completed" && a.difference < 0,
  ).length;
  const totalImpactValue = adjustments
    .filter((a) => a.status === "completed")
    .reduce((sum, a) => sum + a.difference * (a.costPerUnit || 0), 0);

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      stockAdjustmentService.cancel(id, { cancelReason: reason }),
    onSuccess: () => {
      toast.success("Penyesuaian stok berhasil dibatalkan.");
      queryClient.invalidateQueries({ queryKey: ["stock-adjustments"] });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      setCancellingAdjustment(null);
    },
    onError: (err: Error) => {
      toast.error(getErrorMessage(err, "Gagal membatalkan penyesuaian stok."));
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Penyesuaian Stok"
        description="Koreksi langsung saldo persediaan untuk kasus darurat seperti tumpah, rusak, kedaluwarsa, atau temuan fisik."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
              Muat Ulang
            </Button>

            <Button
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs shadow-xs"
            >
              <PlusCircle className="h-4 w-4" />
              Buat Penyesuaian
            </Button>
          </div>
        }
      />

      {/* KPI Cards */}
      <StockAdjustmentSummaryCards
        totalCount={totalCount}
        plusCount={plusCount}
        minusCount={minusCount}
        totalImpactValue={totalImpactValue}
      />

      {/* Table Card */}
      <StockAdjustmentTableCard
        adjustments={adjustments}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        itemTypeFilter={itemTypeFilter}
        onItemTypeFilterChange={setItemTypeFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        bucketFilter={bucketFilter}
        onBucketFilterChange={setBucketFilter}
        onCancelRequest={(adj) => setCancellingAdjustment(adj)}
      />

      {/* Create Dialog */}
      <StockAdjustmentDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["stock-adjustments"] });
          queryClient.invalidateQueries({ queryKey: ["stocks"] });
        }}
      />

      {/* Cancel Dialog */}
      <StockAdjustmentCancelDialog
        adjustment={cancellingAdjustment}
        onOpenChange={(open) => !open && setCancellingAdjustment(null)}
        onConfirm={async (reason) => {
          if (!cancellingAdjustment) return;
          await cancelMutation.mutateAsync({
            id: cancellingAdjustment.id,
            reason,
          });
        }}
      />
    </div>
  );
}
